import { createNavigation } from '../components/navigation.js';
import { createFooter } from '../components/footer.js';

export function createAboutPage(): string {
  return `
    ${createNavigation()}

    <!-- Mission Section -->
    <section class="mission-section">
        <div class="container">
            <h1 class="page-title">Our Mission</h1>
            <div class="mission-content">
                <p class="mission-text">
                    Professional networking today feels more like a transaction than a connection. Platforms like LinkedIn, Calendly, and Handshake focus too much on polish and formality, and not enough on <span class="accent">humanity</span>. But true connection doesn’t come from cold emails or distant Zoom calls. It comes from meeting someone in person, feeling their <span class="accent">vibe</span>, and seeing who they really are.
                </p>
                <p class="mission-text">
                    At <em>VibeChk</em>, we’re building a new way to connect that’s built around <span class="accent">authenticity</span>, spontaneity, and in-person energy. Whether you’re a Gen Z founder, engineer, designer, UGC creator, growth hacker, artist, or VC, our app helps you skip the awkward formality and meet someone for a walk or coffee right now or sometime this week. Every meetup is simple, one on one, and verified with a quick QR scan. The best conversations happen when it’s just two people, focus on each other.
                </p>
                <p class="mission-text">
                    We believe that real-world vibes decide who we trust, collaborate with, and build lasting relationships with. One meaningful meetup beats a hundred cold DMs. That’s why we’re not just another networking tool. We’re a platform for the next era of <span class="accent">creators</span> to find each other in real life and start building something great together.
                </p>
            </div>

        </div>
    </section>

    <!-- Enhanced Twitter Inspiration Section -->
    <section class="inspiration-section">
        <div class="container">
            <div class="inspiration-header">
                <h2 class="section-title">The <span class="accent">Creator</span> Community Speaks</h2>
                <p class="inspiration-subtitle">Real voices, real connections, real impact. Here's what builders are saying about meeting IRL.</p>
            </div>
            
            <div class="tweets-showcase">
                <div class="tweets-carousel">
                    <div class="tweets-container" id="tweets-container">
                        <div class="tweet-slide active" data-slide="0">
                            <div class="tweet-wrapper">
                                <blockquote class="twitter-tweet" data-theme="light" data-conversation="none" data-cards="hidden" data-width="500">
                                    <p lang="en" dir="ltr">My contrarian view:<br>Nothing is real until you meet with them in person<br><br>Even then, there's no absolute genuine, but the signal gets stronger<br><br>IRL experience raises the percentage of truth</p>
                                    &mdash; Justin Liao (@JustinLiao234) 
                                    <a href="https://twitter.com/JustinLiao234/status/1928497686224814163?ref_src=twsrc%5Etfw">November 27, 2024</a>
                                </blockquote>
                            </div>
                        </div>
                        
                        <div class="tweet-slide" data-slide="5">
                            <div class="tweet-wrapper">
                                <blockquote class="twitter-tweet" data-theme="light" data-conversation="none" data-cards="hidden" data-width="500">
                                    <p lang="en" dir="ltr">human IP more important now than ever <br><br>&gt; IRL time spent with your friends<br>&gt; riffing on ideas live in an at person meetings<br>&gt; shared meals with your family<br> &gt; random stranger interactions at airports</p>&mdash; Ali Debow (@ali_debow) <a href="https://twitter.com/ali_debow/status/1938946986880700779?ref_src=twsrc%5Etfw">June 28, 2025</a></blockquote>
                            </div>
                        </div>

                        <div class="tweet-slide" data-slide="1">
                            <div class="tweet-wrapper">
                                <blockquote class="twitter-tweet" data-theme="light" data-conversation="none" data-cards="hidden" data-width="500">
                                    <p lang="en" dir="ltr">Both are needed.<br><br>Coffee chats say "let's get to the point"<br>Walk and talks say "let's get to know each other"<br><br>Completely different vibes, both valuable in their own way. 
                                    <a href="https://t.co/lBzQHBDHov">https://t.co/lBzQHBDHov</a></p>
                                    &mdash; Justin Liao (@JustinLiao234) 
                                    <a href="https://twitter.com/JustinLiao234/status/1928004175582892113?ref_src=twsrc%5Etfw">November 26, 2024</a>
                                </blockquote>
                            </div>
                        </div>
                        
                        <div class="tweet-slide" data-slide="7">
                            <div class="tweet-wrapper">
                                <blockquote class="twitter-tweet" data-theme="light" data-conversation="none" data-cards="hidden" data-width="500">
                                    <p lang="en" dir="ltr">cluely's throwing THE startup school afterparty.<br><br>invite only.<br><br>june 16, 10pm.<br><br>dm me for an invite. <a href="https://t.co/AZFTEVLIn2">pic.twitter.com/AZFTEVLIn2</a></p>&mdash; Roy (@im_roy_lee) <a href="https://twitter.com/im_roy_lee/status/1934014268699271324?ref_src=twsrc%5Etfw">June 14, 2025</a></blockquote>
                            </div>
                        </div>

                        <div class="tweet-slide" data-slide="2">
                            <div class="tweet-wrapper">
                                <blockquote class="twitter-tweet" data-theme="light" data-conversation="none" data-cards="hidden" data-width="500">
                                    <p lang="en" dir="ltr">Your network won't matter until you meet with them in person. <br><br>Why not grab a coffee?</p>
                                    &mdash; Justin Liao (@JustinLiao234) 
                                    <a href="https://twitter.com/JustinLiao234/status/1925244327846191148?ref_src=twsrc%5Etfw">November 18, 2024</a>
                                </blockquote>
                            </div>
                        </div>

                        <div class="tweet-slide" data-slide="9">
                            <div class="tweet-wrapper">
                                <blockquote class="twitter-tweet" data-theme="light" data-conversation="none" data-cards="hidden" data-width="500">
                                    <p lang="en" dir="ltr">If we're in the same city and you default to virtual meetings, that's low signal. I dont want screen time, I want real time<br><br>Reschedule if you need to, but don't confuse attendance w presence</p>&mdash; Emma (@emmainSTEM) <a href="https://twitter.com/emmainSTEM/status/1936260192808976853?ref_src=twsrc%5Etfw">June 21, 2025</a></blockquote>
                            </div>
                        </div>
                        
                        <div class="tweet-slide" data-slide="10">
                            <div class="tweet-wrapper">
                                <blockquote class="twitter-tweet" data-theme="light" data-conversation="none" data-cards="hidden" data-width="500">
                                    <p lang="en" dir="ltr">in nyc for another 2 weeks. growing to like the city. if you're here hmu i wanna have lunch/matcha/dinner with u!!! <a href="https://t.co/RQlCic2WDS">pic.twitter.com/RQlCic2WDS</a></p>&mdash; joyce (@henloitsjoyce) <a href="https://twitter.com/henloitsjoyce/status/1939716836876005504?ref_src=twsrc%5Etfw">June 30, 2025</a></blockquote>
                            </div>
                        </div>

                        <div class="tweet-slide" data-slide="3">
                            <div class="tweet-wrapper">
                                <blockquote class="twitter-tweet" data-theme="light" data-conversation="none" data-cards="hidden" data-width="500">
                                    <p lang="en" dir="ltr">The level of fake, surface-level content by platform: <br><br>LinkedIn ≥ Instagram &gt; X<br><br>It's all fake until you grab a coffee with them irl😉</p>
                                    &mdash; Justin Liao (@JustinLiao234) 
                                    <a href="https://twitter.com/JustinLiao234/status/1927415134546276542?ref_src=twsrc%5Etfw">November 24, 2024</a>
                                </blockquote>
                            </div>
                        </div>
                        
                        <div class="tweet-slide" data-slide="4">
                            <div class="tweet-wrapper">
                                <blockquote class="twitter-tweet" data-theme="light" data-conversation="none" data-cards="hidden" data-width="500">
                                    <p lang="en" dir="ltr">Your network is your potential net worth.<br><br>Without purpose and action it won&#39;t matter.</p>
                                    &mdash; Justin Liao (@JustinLiao234) 
                                    <a href="https://twitter.com/JustinLiao234/status/1929714145529905394?ref_src=twsrc%5Etfw">December 1, 2024</a>
                                </blockquote>
                            </div>
                        </div>

                        <div class="tweet-slide" data-slide="6">
                            <div class="tweet-wrapper">
                                <blockquote class="twitter-tweet" data-theme="light" data-conversation="none" data-cards="hidden" data-width="500">
                                    <p lang="en" dir="ltr">Living in SF for the next few months! Let's hang ✌️ <a href="https://t.co/eB6Yrvd2Tb">pic.twitter.com/eB6Yrvd2Tb</a></p>&mdash; Annabel Strauss (@annabelstrauss) <a href="https://twitter.com/annabelstrauss/status/1939713610948956211?ref_src=twsrc%5Etfw">June 30, 2025</a></blockquote>
                            </div>
                        </div>

                        <div class="tweet-slide" data-slide="8">
                            <div class="tweet-wrapper">
                                <blockquote class="twitter-tweet" data-theme="light" data-conversation="none" data-cards="hidden" data-width="500">
                                    <p lang="en" dir="ltr">Deep networking will never work through cold DMs or email,<br><br>irl experience and vibes are keys.</p>&mdash; Justin Liao (@JustinLiao234) <a href="https://twitter.com/JustinLiao234/status/1939014641725906991?ref_src=twsrc%5Etfw">June 28, 2025</a></blockquote>
                            </div>
                        </div>

                    </div>

                    <!-- Navigation Controls -->
                    <div class="carousel-controls">
                        <div class="carousel-indicators">
                            <button class="indicator active" data-slide="0" aria-label="View tweet 1"></button>
                            <button class="indicator" data-slide="1" aria-label="View tweet 2"></button>
                            <button class="indicator" data-slide="2" aria-label="View tweet 3"></button>
                            <button class="indicator" data-slide="3" aria-label="View tweet 4"></button>
                            <button class="indicator" data-slide="4" aria-label="View tweet 5"></button>
                            <button class="indicator" data-slide="5" aria-label="View tweet 6"></button>
                            <button class="indicator" data-slide="6" aria-label="View tweet 7"></button>
                            <button class="indicator" data-slide="7" aria-label="View tweet 8"></button>
                            <button class="indicator" data-slide="8" aria-label="View tweet 9"></button>
                            <button class="indicator" data-slide="9" aria-label="View tweet 10"></button>
                            <button class="indicator" data-slide="10" aria-label="View tweet 11"></button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="community-cta">
                <a class="community-button" href="https://discord.gg/jkKmgKqzua" target="_blank" rel="noopener noreferrer">
                    Join our Community
                </a>
            </div>
        </div>
    </section>

    <!-- Our Team Section -->
    <section class="team-section">
        <div class="container">
            <h2 class="section-title">Our Team</h2>
            <p class="team-subtitle">Meet the creators behind the movement</p>
            
            <div class="team-grid">
                <div class="team-card">
                    <div class="team-image">
                        <img src="https://pbs.twimg.com/profile_images/1964568693431734272/oiNrUp7M_400x400.jpg" alt="Justin Liao" />
                    </div>
                    <div class="team-info">
                        <h3 class="team-name">Justin Liao</h3>
                        <p class="team-title">Co-Founder</p>
                        <div class="team-social">
                            <a href="https://x.com/imjustinliao" aria-label="Twitter/X" class="social-link">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                            </a>
                            <a href="https://justinliao.me/" aria-label="Website" class="social-link">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>

                <div class="team-card">
                    <div class="team-image">
                        <img src="https://volun-tier.com/Thomas.jpg" alt="Thomas Suen" />
                    </div>
                    <div class="team-info">
                        <h3 class="team-name">Thomas Suen</h3>
                        <p class="team-title">Co-Founder</p>
                        <div class="team-social">
                            <a href="https://x.com/owenisas" aria-label="Twitter/X" class="social-link">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                            </a>
                            <a href="https://www.owenisas.com/" aria-label="Website" class="social-link">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    ${createFooter()}
    
    <!-- Load Twitter widget script -->
    <script async src="https://platform.twitter.com/widgets.js" charset="utf-8"></script>
    

    

  `;
} 